'use client'

import Link from 'next/link'
import { motion } from 'framer-motion'
import { FaSearchPlus, FaArrowRight } from 'react-icons/fa'
import { track } from '@/utils/analytics'

/**
 * IngredientCheckerPromo — a contextual nudge toward the web tool that
 * doesn't require installing anything.
 *
 * Why this exists: GSC shows "microplastic ingredient checker" ranks at
 * position 5 with 40% CTR — the tool itself converts (8.1% page CTR,
 * 2.6 completes per user) — but only 5 users found it in the last 30
 * days because almost nothing on the site internal-links to it. The
 * cosmetics-topic blog cluster has the highest contextual overlap with
 * the tool (both deal with ingredient lists, polymers, "is X a
 * microplastic?" type questions). Adding a one-line callout to those
 * articles is the highest-leverage internal-link the site can ship
 * without touching individual article files.
 *
 * Visual: a compact strip — single CTA, no large hero. Slips above the
 * FirstScanCallout / bottomCta so it offers the "you can use this RIGHT
 * NOW without installing anything" path before the app pitch.
 *
 * Tracking: standard analytics helper with cta_surface =
 * `ingredient_checker_promo`, post slug + topic for breakdown. The tool's
 * own page has its own funnel events (ingredient_checker_start /
 * _complete) so end-to-end conversion is measurable.
 */
export interface IngredientCheckerPromoProps {
  /** Slug of the post the promo is rendered on (for attribution). */
  postSlug: string
  /** Topic of the post (for attribution). */
  postTopic: string
}

export default function IngredientCheckerPromo({
  postSlug,
  postTopic,
}: IngredientCheckerPromoProps) {
  return (
    <motion.aside
      aria-label="Free ingredient checker tool"
      className="not-prose mt-10 flex items-center gap-4 rounded-2xl border border-charcoal/10 bg-white px-4 sm:px-5 py-4 sm:py-5 hover:border-warmOrange/40 hover:shadow-md transition-[color,background-color,border-color,text-decoration-color,box-shadow]"
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4 }}
      onViewportEnter={() =>
        track('ingredient_checker_promo_impression', {
          cta_surface: 'ingredient_checker_promo',
          post_slug: postSlug,
          post_topic: postTopic,
          page_type: 'blog',
        })
      }
    >
      <span
        aria-hidden="true"
        className="shrink-0 inline-flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-warmOrange/15 text-warmOrange"
      >
        <FaSearchPlus className="w-5 h-5" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-2xs sm:text-xs font-semibold uppercase tracking-[0.18em] text-warmOrange mb-0.5">
          Free · No install
        </p>
        <p className="text-base sm:text-base font-semibold text-charcoal leading-snug">
          Paste any ingredient list, flag every plastic polymer in one tap.
        </p>
      </div>
      <Link
        href="/microplastic-ingredient-checker"
        onClick={() =>
          track('related_article_click', {
            cta_surface: 'ingredient_checker_promo',
            destination: '/microplastic-ingredient-checker',
            post_slug: postSlug,
            post_topic: postTopic,
            page_type: 'blog',
          })
        }
        className="shrink-0 inline-flex items-center gap-1.5 text-sm sm:text-base font-semibold text-charcoal hover:text-warmOrange transition-colors min-h-[44px] px-3 sm:px-4 rounded-lg"
      >
        Try it
        <FaArrowRight className="w-3 h-3" aria-hidden="true" />
      </Link>
    </motion.aside>
  )
}
